import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Phone, PhoneOutgoing, Edit, Trash2, UserPlus, Star, Loader2, Clock } from 'lucide-react'
import { Contact, CallHistoryItem } from '@/types/account'
import { useInfiniteCallHistoryQuery } from '@/hooks/useInfiniteCallHistoryQuery'
import { useContactLookup } from '@/hooks/useContactLookup'
import { useUpdateContact, useDeleteContact } from '@/hooks/useContacts'
import { accountQueries } from '@/services/accountApi'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { formatCurrencyFromCents } from '@/lib/currency'
import { getCurrencyConfig } from '@/config/currency'
import { formatDuration, formatTimeAgo } from '@/lib/format'
import { CONTACT_TYPE_CLASSES } from './phoneUtils'
import { AvatarPlaceholder } from '@/components/ui/avatar-placeholder'
import ContactForm from './ContactForm'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ContactDetailState {
  contact: Contact | null
  phoneNumber: string      // the number that was clicked (entry point)
}

interface ContactDetailPanelProps {
  detail: ContactDetailState
  onBack: () => void
  onCall: (number: string) => void
  onContactUpdated?: () => void
}

// ---------------------------------------------------------------------------
// AddToContactDialog (reused from HistoryPanel, but self-contained here)
// ---------------------------------------------------------------------------

interface AddToContactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  phoneNumber: string
  onSuccess: () => void
}

const AddToContactDialog: React.FC<AddToContactDialogProps> = ({ open, onOpenChange, phoneNumber, onSuccess }) => {
  const { t } = useTranslation(['app', 'common'])
  const { contacts } = useContactLookup()
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState<string | null>(null)

  const filtered = contacts.filter((c) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ').toLowerCase().includes(search.toLowerCase())
  )

  const handleAdd = async (contact: Contact) => {
    if (saving) return
    try {
      setSaving(contact.id)
      const { contactsApiService } = await import('@/services/contactsApi')
      await contactsApiService.updateContact(contact.id, {
        phoneNumbers: [...(contact.phoneNumbers ?? []), { number: phoneNumber, type: 'mobile' }],
      })
      toast.success(t('contacts.addToContactSuccess', { name: [contact.firstName, contact.lastName].filter(Boolean).join(' ') }))
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('contacts.addToContactError'))
    } finally {
      setSaving(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader><DialogTitle>{t('contacts.addToContactTitle')}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground mb-2 font-mono">{phoneNumber}</div>
        <div className="relative mb-3">
          <Input autoFocus placeholder={t('contacts.selectContact')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-border rounded-md border border-border">
          {filtered.length === 0 ? (
            <p className="p-4 text-sm text-center text-muted-foreground">{t('common.noResults')}</p>
          ) : filtered.map((contact) => {
            const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || t('contacts.unnamed')
            return (
              <button
                key={contact.id}
                onClick={() => handleAdd(contact)}
                disabled={!!saving}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors disabled:opacity-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{name}</div>
                  {contact.phoneNumbers?.[0] && (
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {contact.phoneNumbers[0].number}
                      {contact.phoneNumbers.length > 1 && ` +${contact.phoneNumbers.length - 1}`}
                    </div>
                  )}
                </div>
                {saving === contact.id && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />}
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// ContactDetailPanel
// ---------------------------------------------------------------------------

const ContactDetailPanel: React.FC<ContactDetailPanelProps> = ({ detail, onBack, onCall, onContactUpdated }) => {
  const { t } = useTranslation(['app', 'common'])
  const queryClient = useQueryClient()
  const { contact, phoneNumber } = detail

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false)
  const [isNewContactOpen, setIsNewContactOpen] = useState(false)
  const [isAddToOpen, setIsAddToOpen] = useState(false)

  const updateContact = useUpdateContact()
  const deleteContact = useDeleteContact()

  // Call history for this number
  const { calls, isLoading: isLoadingCalls, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteCallHistoryQuery({
    query: phoneNumber,
  })

  // Invalidation for operations that bypass React Query mutations (ContactForm, AddToContactDialog)
  const invalidateContactCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: accountQueries.contactsLookupBase })
    queryClient.invalidateQueries({ queryKey: accountQueries.contactsBase })
    queryClient.invalidateQueries({ queryKey: accountQueries.contactsInfiniteBase })
    queryClient.invalidateQueries({ queryKey: accountQueries.contactLabelsBase })
    onContactUpdated?.()
  }, [queryClient, onContactUpdated])

  const afterMutate = useCallback(() => {
    onContactUpdated?.()
  }, [onContactUpdated])

  const handleDelete = async () => {
    if (!contact) return
    try {
      await deleteContact.mutateAsync(contact.id)
      toast.success(t('contacts.deleteSuccess', 'Contact deleted'))
      afterMutate()
      onBack()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('contacts.deleteError', 'Failed to delete'))
    } finally {
      setIsDeleteConfirming(false)
    }
  }

  const handleToggleFavourite = async () => {
    if (!contact || updateContact.isPending) return
    try {
      const isFav = contact.labels?.includes('favourite') ?? false
      const newLabels = isFav
        ? (contact.labels ?? []).filter(l => l !== 'favourite')
        : [...(contact.labels ?? []), 'favourite']
      await updateContact.mutateAsync({ contactId: contact.id, payload: { labels: newLabels as any } })
      afterMutate()
    } catch (err) {
      toast.error(t('contacts.favouriteError', 'Failed to update favourite'))
    }
  }

  // Avatar / hero
  const displayName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || t('contacts.unnamed')
    : phoneNumber

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-background shrink-0">
        <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0 -ml-1">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-medium text-muted-foreground">
          {contact ? t('contacts.contact', 'Contact') : t('contacts.unknownNumber', 'Unknown number')}
        </span>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Hero */}
        <div className="flex flex-col items-center pt-8 pb-6 px-6 text-center">
          {contact
            ? <AvatarPlaceholder seed={displayName} size={80} className="mb-4 shrink-0" />
            : <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 shrink-0 bg-muted">
                <Phone className="w-8 h-8 text-muted-foreground" />
              </div>
          }
          <h2 className="text-xl font-semibold text-foreground leading-tight">
            {contact ? displayName : <span className="font-mono">{phoneNumber}</span>}
          </h2>
          {contact && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap justify-center">
              {contact.labels?.includes('favourite') && (
                <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <Star className="w-3 h-3 fill-current" /> Favourite
                </span>
              )}
              {contact.labels?.filter(l => l !== 'favourite').map(l => (
                <Badge key={l} variant="secondary" className="capitalize text-xs">{l}</Badge>
              ))}
            </div>
          )}
          {contact && (
            <div className="flex items-center gap-2 mt-4">
              <Button
                variant="ghost" size="sm"
                onClick={handleToggleFavourite}
                disabled={updateContact.isPending}
                className="h-8 gap-1.5"
              >
                <Star className={cn('w-3.5 h-3.5', contact.labels?.includes('favourite') ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground')} />
                <span className="text-xs">{contact.labels?.includes('favourite') ? t('contacts.unfavourite', 'Unfavourite') : t('contacts.favourite', 'Favourite')}</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsEditOpen(true)} className="h-8 gap-1.5">
                <Edit className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs">{t('contacts.edit')}</span>
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => setIsDeleteConfirming(true)}
                className="h-8 gap-1.5 text-destructive hover:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="text-xs">{t('contacts.delete')}</span>
              </Button>
            </div>
          )}
        </div>

        {/* Phone numbers */}
        <div className="px-4 pb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {contact ? t('contacts.phoneNumbers', 'Phone numbers') : t('contacts.number', 'Number')}
          </h3>
          <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
            {contact ? (
              contact.phoneNumbers.length === 0 ? (
                <div className="px-4 py-3 text-sm text-muted-foreground italic">{t('contacts.noPhoneNumbers')}</div>
              ) : (
                contact.phoneNumbers.map((pn, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm text-foreground">{pn.number}</div>
                      {pn.type && (
                        <Badge variant="outline" className={cn('text-xs capitalize mt-0.5 py-0 h-4', CONTACT_TYPE_CLASSES[pn.type])}>
                          {pn.type}
                        </Badge>
                      )}
                    </div>
                    <Button size="sm" onClick={() => onCall(pn.number)} className="h-8 w-8 p-0 rounded-full">
                      <Phone className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )
            ) : (
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-foreground">{phoneNumber}</div>
                </div>
                <Button size="sm" onClick={() => onCall(phoneNumber)} className="h-8 w-8 p-0 rounded-full">
                  <Phone className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>

          {/* Save options for unknown numbers */}
          {!contact && (
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setIsNewContactOpen(true)}>
                <UserPlus className="w-3.5 h-3.5" />
                {t('contacts.saveAsNew')}
              </Button>
              <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setIsAddToOpen(true)}>
                <UserPlus className="w-3.5 h-3.5" />
                {t('contacts.addToContact')}
              </Button>
            </div>
          )}
        </div>

        {/* Delete confirmation inline */}
        {isDeleteConfirming && (
          <div className="mx-4 mb-4 p-4 rounded-xl border border-destructive/30 bg-destructive/5">
            <p className="text-sm font-medium text-foreground mb-1">{t('contacts.deleteConfirmTitle')}</p>
            <p className="text-xs text-muted-foreground mb-3">{t('contacts.deleteConfirmMessage')}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => setIsDeleteConfirming(false)} disabled={deleteContact.isPending}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" size="sm" className="flex-1" onClick={handleDelete} disabled={deleteContact.isPending}>
                {deleteContact.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                {t('contacts.delete')}
              </Button>
            </div>
          </div>
        )}

        {/* Call history */}
        <div className="px-4 pb-6">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t('account.callHistory')}
          </h3>
          <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
            {isLoadingCalls ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-20" />
                    <Skeleton className="h-3 w-14" />
                  </div>
                  <Skeleton className="h-3 w-10" />
                </div>
              ))
            ) : calls.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                {t('account.noCallHistory')}
              </div>
            ) : (
              calls.map((call) => (
                <DetailCallRow key={call.id} call={call} onCall={onCall} />
              ))
            )}
          </div>
          {hasNextPage && (
            <Button
              variant="ghost" size="sm"
              className="w-full mt-2 text-muted-foreground"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : t('common.loadMore', 'Load more')
              }
            </Button>
          )}
        </div>
      </div>

      {/* Edit contact dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>{t('contacts.editContact')}</DialogTitle></DialogHeader>
          {contact && (
            <ContactForm
              contact={contact}
              onSuccess={() => { setIsEditOpen(false); invalidateContactCaches() }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Save as new contact dialog */}
      <Dialog open={isNewContactOpen} onOpenChange={setIsNewContactOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle>{t('contacts.newContact')}</DialogTitle></DialogHeader>
          <ContactForm
            initialPhoneNumber={phoneNumber}
            onSuccess={() => { setIsNewContactOpen(false); invalidateContactCaches() }}
          />
        </DialogContent>
      </Dialog>

      {/* Add to existing contact dialog */}
      <AddToContactDialog
        open={isAddToOpen}
        onOpenChange={setIsAddToOpen}
        phoneNumber={phoneNumber}
        onSuccess={() => { setIsAddToOpen(false); invalidateContactCaches() }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// DetailCallRow — compact call item for the detail panel
// ---------------------------------------------------------------------------

interface DetailCallRowProps {
  call: CallHistoryItem
  onCall: (number: string) => void
}

const DetailCallRow: React.FC<DetailCallRowProps> = ({ call, onCall }) => {
  const { t } = useTranslation(['app', 'common'])
  const duration = formatDuration(call.duration)
  const timeAgo = formatTimeAgo(call.startTime, t('account.justNow'))

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-muted flex items-center justify-center">
        <PhoneOutgoing className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground">{timeAgo}</div>
        {duration && <div className="text-xs text-muted-foreground">{duration}</div>}
        {call.cost?.amount != null && call.cost.amount > 0 && (
          <div className="text-xs text-muted-foreground">
            {formatCurrencyFromCents(Math.abs(call.cost.amount), {
              currency: getCurrencyConfig(call.cost.currency),
              useIntl: true,
            })}
          </div>
        )}
      </div>
      <Button size="sm" variant="ghost" onClick={() => onCall(call.destinationNumber)} className="h-7 w-7 p-0 rounded-full shrink-0">
        <Phone className="w-3.5 h-3.5 text-primary" />
      </Button>
    </div>
  )
}

export default ContactDetailPanel
